import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { supabase } from "../config/supabaseClient";

// Async thunk to fetch race groups


// Async thunk to fetch EAP configuration data
export const fetchEAPConfiguration = createAsyncThunk(
  "raceGroups/fetchEAPConfiguration",
  async ({ provinceName, isNational }, { rejectWithValue }) => {
    try {
      
      let data, error;
      if (isNational) {
        // For national data, query with null province_name
        const result = await supabase
          .from("eap_configuration")
          .select("*")
          .is("province_name", null)
          .eq("is_national", true);
        data = result.data;
        error = result.error;
      } else {
        // For province data, query with specific province_name
        const result = await supabase
          .from("eap_configuration")
          .select("*")
          .eq("province_name", provinceName)
          .eq("is_national", false);
        data = result.data;
        error = result.error;
      }
      
      if (error) {
        console.error("Fetch error:", error);
        throw error;
      }
      return data;
    } catch (err) {
      console.error("Fetch catch error:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async thunk to insert EAP configuration data
export const insertEAPConfiguration = createAsyncThunk(
  "raceGroups/insertEAPConfiguration",
  async (insertData, { rejectWithValue }) => {
    try {
      const { data: responseData, error } = await supabase
        .from("eap_configuration")
        .insert(insertData)
        .select();
      if (error) {
        console.error("Insert error:", error);
        throw error;
      }
      return responseData;
    } catch (err) {
      console.error("Insert catch error:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async thunk to update existing EAP configuration data (delete then insert)
export const updateEAPConfiguration = createAsyncThunk(
  "raceGroups/updateEAPConfiguration",
  async (upsertData, { rejectWithValue }) => {
    try {
      
      // First, delete existing records for the same province_name and is_national combination
      const firstRecord = upsertData[0];
      
      let deleteError = null;
      if (firstRecord.province_name === null) {
        // For national data, delete records with null province_name
        const { error } = await supabase
          .from("eap_configuration")
          .delete()
          .is("province_name", null)
          .eq("is_national", true);
        deleteError = error;
      } else {
        // For province data, delete records with specific province_name
        const { error } = await supabase
          .from("eap_configuration")
          .delete()
          .eq("province_name", firstRecord.province_name)
          .eq("is_national", false);
        deleteError = error;
      }
      
      if (deleteError) {
        console.error("Delete error:", deleteError);
        throw deleteError;
      }

      // Then insert the new records
      const { data: responseData, error } = await supabase
        .from("eap_configuration")
        .insert(upsertData)
        .select();
      
      if (error) {
        console.error("Update insert error:", error);
        throw error;
      }
      return responseData;
    } catch (err) {
      console.error("Update catch error:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async thunk to fetch company user demographics
export const fetchCompanyUserDemographics = createAsyncThunk(
  "raceGroups/fetchCompanyUserDemographics",
  async (_, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("company_user")
        .select("Race, Gender");
      
      if (error) {
        console.error("Fetch demographics error:", error);
        throw error;
      }
      
      // Process the data to count by race and gender
      const demographics = {};
      data.forEach(user => {
        const race = user.Race || 'Unknown';
        const gender = user.Gender || 'Unknown';
        
        if (!demographics[race]) {
          demographics[race] = { male: 0, female: 0, total: 0 };
        }
        
        if (gender.toLowerCase() === 'male') {
          demographics[race].male++;
        } else if (gender.toLowerCase() === 'female') {
          demographics[race].female++;
        }
        
        demographics[race].total++;
      });
      
      return demographics;
    } catch (err) {
      console.error("Fetch demographics catch error:", err);
      return rejectWithValue(err.message);
    }
  }
);

const raceGroupSlice = createSlice({
  name: "raceGroups",
  initialState: {
    raceGroups: [],
    loading: false,
    error: null,
    eapConfiguration: [],
    eapLoading: false,
    eapError: null,
  },
  reducers: {
    clearEAPError: (state) => {
      state.eapError = null;
    },
    clearRaceGroupsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch EAP Configuration cases
      .addCase(fetchEAPConfiguration.pending, (state) => {
        state.eapLoading = true;
        state.eapError = null;
      })
      .addCase(fetchEAPConfiguration.fulfilled, (state, action) => {
        state.eapLoading = false;
        state.eapConfiguration = action.payload;
      })
      .addCase(fetchEAPConfiguration.rejected, (state, action) => {
        state.eapLoading = false;
        state.eapError = action.payload;
      })
      // Insert EAP Configuration cases
      .addCase(insertEAPConfiguration.pending, (state) => {
        state.eapLoading = true;
        state.eapError = null;
      })
      .addCase(insertEAPConfiguration.fulfilled, (state, action) => {
        state.eapLoading = false;
        state.eapConfiguration = action.payload;
      })
      .addCase(insertEAPConfiguration.rejected, (state, action) => {
        state.eapLoading = false;
        state.eapError = action.payload;
      })
      // Update EAP Configuration cases
      .addCase(updateEAPConfiguration.pending, (state) => {
        state.eapLoading = true;
        state.eapError = null;
      })
      .addCase(updateEAPConfiguration.fulfilled, (state, action) => {
        state.eapLoading = false;
        state.eapConfiguration = action.payload;
      })
      .addCase(updateEAPConfiguration.rejected, (state, action) => {
        state.eapLoading = false;
        state.eapError = action.payload;
      });
  },
});

export const { clearEAPError, clearRaceGroupsError } = raceGroupSlice.actions;
export default raceGroupSlice.reducer;